import React, { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { Button, FormControl, Modal } from 'react-bootstrap'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { DraExportData } from 'plugin-codaf-risk-ext-bowtie/services/export'
import { CustomList, RemoveConfirmationModal } from 'plugin-generic-components/shared/components/components'

import { AssetSelector, FilteredSearch, ImportDataModal, IncompleteExportConfimationModal } from '../../../generic'
import {
    deleteBarrier,
    listBarriers,
    listAssets,
    IAssetList,
    getUpdateModeMethods,
    importBarrierData
} from '../../../services'
import { IAsset, IBarrier, IPaginatedREST } from '../../../types'
import { capitalize } from '../../../utils'
import { barrierEditorQueryStringKey, PAGINATION_SIZE } from '../../../utils/constants'
import { BowtieSettingsRouteContext, IBowtieSettingsRouteContext, IQueryParamsList } from '../../BowtieSettingWrapper'
import { AddBarrierModal } from './components'
import { BarrierListContainer } from './styles'

export const BarrierList: React.FC = () => {
    const [request, setRequest] = useState<IPaginatedREST<IBarrier>>()
    const [assetRequest, setAssetRequest] = useState<IPaginatedREST<IAsset>>()

    const [asset, setAsset] = useState<IAsset>()
    const [updateModeMethods, setUpdateModeMethods] = useState<{ id: number; value: string; label: string }[]>([])
    const [tempItem, setTempItem] = useState<IBarrier>()
    const [showRemoveModal, setShowRemoveModal] = useState(false)
    const [showAddModal, setShowAddModal] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [exportImportLoading, setExportImportLoading] = useState<boolean>(false)
    const [showIncompleteExportConfimationModal, setShowIncompleteExportConfimationModal] = useState(false)
    const [files, setFiles] = useState<FileList>(null)

    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const bowtieManager = authorization.Permission.BOWTIE_MANAGER

    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsList>>(BowtieSettingsRouteContext)
    const [assetParams, setAssetParams] = useState<IAssetList>({ keyword: '' })

    const handleCloseProtocol = (): void => {
        setShowImportModal(false)
        setFiles(null)
    }

    const handleGetBarriersList = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await listBarriers(params)
            setRequest(data)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get barriers list.'))
        } finally {
            setLoading(false)
        }
    }

    const handleGetAssetList = async (assetParams: IAssetList): Promise<void> => {
        try {
            const response = await listAssets({ ...assetParams, orderBy: 'ID', direction: 'ASC' })
            setAssetRequest(response)

            if (!asset && !params.filterBy) {
                setAsset(response?.data?.[0])
            } else if (params.filterBy) {
                setAsset(response?.data?.find(el => el.id === params.filterBy))
            }
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get resource list.'))
        }
    }

    const submitImportData = async (file: File): Promise<void> => {
        try {
            if (file == null) {
                return globalNotification.error(i18n('Please select a file.'))
            }
            setExportImportLoading(true)
            await importBarrierData(file)
            globalNotification.success(i18n('Data imported successfully.'))
            handleGetBarriersList()
            handleCloseProtocol()
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on importing Risk Diagram data.'))
            console.log(error)
        } finally {
            setExportImportLoading(false)
        }
    }
    const handleExportData = async (): Promise<void> => {
        try {
            setExportImportLoading(true)
            await DraExportData({ entityType: 'barrier', acceptEmptyArguments: 'false' })
        } catch (error) {
            if (error.message.toLowerCase().includes('there is at least one')) {
                setShowIncompleteExportConfimationModal(true)
                globalNotification.error(i18n('Confirm incomplete export'))
            } else {
                globalNotification.error(error.message ?? i18n('An error occurred on exporting Barrier data.'))
                console.log(error)
            }
        } finally {
            setExportImportLoading(false)
        }
    }

    const handleGetUpdateModeMethods = async (): Promise<void> => {
        try {
            const response = await getUpdateModeMethods()
            const newOptionArray = response.data.map(el => {
                return {
                    id: el.id,
                    value: el.method,
                    label: capitalize(el.method.toString())
                }
            })

            setUpdateModeMethods(newOptionArray)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get update mode methods.'))
        }
    }

    const handleDeleteItem = (k: IBarrier): void => {
        setTempItem(k)
        setShowRemoveModal(true)
    }

    const handleNewItem = (k?: IBarrier): void => {
        setTempItem(k)
        setShowAddModal(true)
    }

    const handleCloseDeleteItem = (): void => {
        handleGetBarriersList()
        setShowRemoveModal(false)
    }

    const handleCloseAddItemModal = (): void => {
        setShowAddModal(false)
        setTempItem(null)
    }

    const handleAssetSearch = (keyword: string): void => {
        setAssetParams({ keyword })
    }

    const handleGoToEdit = (k: { id: number }): void => {
        localStorage.setItem(barrierEditorQueryStringKey, JSON.stringify(params))
        setParams({ type: 'edit', tab: 'barriers', id: k.id })
    }

    useLayoutEffect(() => {
        handleGetUpdateModeMethods()
    }, [])

    useEffect(() => {
        handleGetAssetList(assetParams)
    }, [assetParams]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (assetRequest != null) {
            if (params.filterBy && params.filterBy > 0) {
                handleGetBarriersList()
            } else {
                if (asset) {
                    setAsset({ ...asset })
                } else if ((assetRequest?.data ?? []).length > 0) {
                    setAsset(assetRequest.data[0])
                }
            }
        }
    }, [params, assetRequest]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (asset) {
            setParams({ ...params, filterBy: asset.id })
        }
    }, [asset]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <BarrierListContainer>
                <div className="asset-selector">
                    <AssetSelector
                        assets={assetRequest?.data ?? []}
                        selectedAsset={asset}
                        onAssetSelect={setAsset}
                        onSearchClick={handleAssetSearch}
                    />
                </div>
                <div className="barriers-list">
                    <div className="barriers-list-header">
                        <div className="barriers-list-search">
                            {bowtieAdmin && (
                                <Button
                                    className="no-outline-button"
                                    bsStyle="primary"
                                    onClick={() => handleNewItem()}
                                    id="barrierList-list-button-newBarrier"
                                    style={{ marginRight: 8 }}
                                    disabled={loading}
                                >
                                    <i className="far fa-plus custom-plus" /> {i18n('New Barrier')}
                                </Button>
                            )}
                            <FilteredSearch
                                keyword={params.keyword}
                                inputId="barrierList-list-field-barrierSearch"
                                onSearch={keyword => setParams({ ...params, keyword })}
                            />
                            {(bowtieAdmin || bowtieManager) && (
                                <Button
                                    className="export-import-button"
                                    onClick={() => {
                                        handleExportData()
                                    }}
                                >
                                    {exportImportLoading ? (
                                        <i className="fa fa-spinner fa-spin fa-x2" />
                                    ) : (
                                        <>
                                            <i className="fas fa-download" /> {i18n('Export data')}
                                        </>
                                    )}
                                </Button>
                            )}
                            {bowtieAdmin && (
                                <Button
                                    className="export-import-button"
                                    onClick={() => {
                                        setShowImportModal(old => !old)
                                    }}
                                >
                                    {exportImportLoading ? (
                                        <i className="fa fa-spinner fa-spin fa-x2" />
                                    ) : (
                                        <>
                                            <i className="fas fa-upload" /> {i18n('Import data')}
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                        <div className="barriers-list-asset-title">
                            {asset?.name ?? 'Resource'} <div className="asset-subtitle">{i18n('BARRIERS')}</div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="loading-spin">
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        </div>
                    ) : request?.data?.length > 0 ? (
                        <CustomList
                            className="custom-list-barrier"
                            list={request.data}
                            columns={[
                                { listRef: 'id', title: 'ID', sortable: true },
                                { listRef: 'name', title: 'Barriers', sortable: true }
                            ]}
                            label="Barriers"
                            onSortClick={(orderBy, direction) => setParams({ ...params, orderBy, direction })}
                            sortDirection={params.direction}
                            sortField={params.orderBy}
                            configOptions={
                                bowtieAdmin
                                    ? [
                                          { icon: 'far fa-edit', onClickConfig: handleGoToEdit },
                                          { icon: 'far fa-copy', onClickConfig: handleNewItem },
                                          { icon: 'far fa-trash-alt', onClickConfig: handleDeleteItem }
                                      ]
                                    : [
                                          {
                                              icon: 'fas fa-eye',
                                              onClickConfig: handleGoToEdit
                                          }
                                      ]
                            }
                            pagination={{
                                type: 'live',
                                totalItems: request.total,
                                showingItems: params.perPage,
                                onPerPageChange: () =>
                                    setParams({ ...params, perPage: params.perPage += PAGINATION_SIZE })
                            }}
                            fixedHeader={true}
                        />
                    ) : (
                        <div className="no-barrier">
                            <i className="far fa-folder-open " />
                            <p>{i18n('There are no barriers yet')}</p>
                            <a onClick={() => setShowAddModal(true)}>{i18n('Create new barrier')}</a>
                        </div>
                    )}
                </div>
            </BarrierListContainer>

            <AddBarrierModal
                handleClose={handleCloseAddItemModal}
                handleSave={id => handleGoToEdit({ id })}
                show={showAddModal}
                item={tempItem}
                asset={asset}
                assets={assetRequest?.data}
                updateModeMethods={updateModeMethods}
            />

            <RemoveConfirmationModal
                handleClose={handleCloseDeleteItem}
                onDelete={async () => await deleteBarrier(tempItem.id)}
                show={showRemoveModal}
                setShow={setShowRemoveModal}
                type="barrier"
                removalMessage={`${i18n('Are you sure you want remove the barrier')} ${tempItem?.id}?`}
            />
            <ImportDataModal
                handleClose={handleCloseProtocol}
                show={showImportModal}
                submit={submitImportData}
                type={'barrier'}
                files={files}
                setFiles={setFiles}
            />

            <IncompleteExportConfimationModal
                setLoading={setExportImportLoading}
                show={showIncompleteExportConfimationModal}
                handleClose={() => setShowIncompleteExportConfimationModal(false)}
                type={'barrier'}
            />
        </>
    )
}
